import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "./firebase";

// Cache for video/thumbnail URLs to avoid repeated requests
const urlCache = new Map();

// Base paths for content in Firebase Storage
const VIDEOS_BASE_PATH = "dashone_tutorial_videos";
const THUMBNAILS_BASE_PATH = "dashone_tutorial_thumbnails";
const WORKFLOW_VIDEOS_BASE_PATH = "dashai_workflow_videos";
const WORKFLOW_SLIDES_BASE_PATH = "dashai_workflow_slides";

// Reject filenames that contain path separators or traversal segments.
// All callers pass static, source-controlled strings; this guard fails fast
// at the helper boundary so the trust assumption is enforced here rather
// than relying on Firebase ref normalisation + storage.rules single-segment
// wildcards downstream.
const assertSafeFileName = (fileName) => {
  if (
    typeof fileName !== "string" ||
    fileName.length === 0 ||
    fileName.includes("/") ||
    fileName.includes("\\") ||
    fileName.includes("..")
  ) {
    throw new Error(`Invalid storage filename: ${fileName}`);
  }
};

/**
 * Get a secure download URL for a video or thumbnail from Firebase Storage
 * @param {string} fileName - The file name in storage
 * @param {string} type - Either 'video' or 'thumbnail' (default: 'video')
 * @returns {Promise<string>} - The download URL
 */
export const getVideoUrl = async (fileName, type = 'video') => {
  assertSafeFileName(fileName);
  const cacheKey = `${type}:${fileName}`;
  
  // Check cache first
  if (urlCache.has(cacheKey)) {
    return urlCache.get(cacheKey);
  }

  try {
    const basePath = type === 'thumbnail' ? THUMBNAILS_BASE_PATH : VIDEOS_BASE_PATH;
    const fileRef = ref(storage, `${basePath}/${fileName}`);
    const url = await getDownloadURL(fileRef);
    
    // Cache the URL
    urlCache.set(cacheKey, url);
    
    return url;
  } catch (error) {
    console.error(`Error fetching ${type} URL for ${fileName}:`, error);
    throw error;
  }
};

/**
 * Get a download URL for a DashAI workflow agent demo video.
 * Workflow demos live under a different Storage prefix from tutorial videos
 * so they can be governed by their own rule and uploaded independently.
 *
 * @param {string} fileName - File name under dashai_workflow_videos/
 * @returns {Promise<string>} - The download URL
 */
export const getWorkflowVideoUrl = async (fileName) => {
  assertSafeFileName(fileName);
  const cacheKey = `workflow:${fileName}`;

  if (urlCache.has(cacheKey)) {
    return urlCache.get(cacheKey);
  }

  try {
    const fileRef = ref(storage, `${WORKFLOW_VIDEOS_BASE_PATH}/${fileName}`);
    const url = await getDownloadURL(fileRef);
    urlCache.set(cacheKey, url);
    return url;
  } catch (error) {
    console.error(`Error fetching workflow video URL for ${fileName}:`, error);
    throw error;
  }
};

/**
 * Get a download URL for a DashAI workflow example slide.
 * Slides live under their own Storage prefix (`dashai_workflow_slides/`) gated
 * by `storage.rules` to authenticated reads, mirroring the workflow video flow.
 *
 * @param {string} fileName - File name under `dashai_workflow_slides/`
 * @returns {Promise<string>} - The download URL
 */
export const getWorkflowSlideUrl = async (fileName) => {
  assertSafeFileName(fileName);
  const cacheKey = `slide:${fileName}`;

  if (urlCache.has(cacheKey)) {
    return urlCache.get(cacheKey);
  }

  try {
    const fileRef = ref(storage, `${WORKFLOW_SLIDES_BASE_PATH}/${fileName}`);
    const url = await getDownloadURL(fileRef);
    urlCache.set(cacheKey, url);
    return url;
  } catch (error) {
    console.error(`Error fetching workflow slide URL for ${fileName}:`, error);
    throw error;
  }
};

/**
 * Resolve an example-slide reference to a usable image URL.
 *
 * Entries beginning with '/' are local assets served from the web root
 * (public/) and are returned unchanged. Everything else is a filename under
 * `dashai_workflow_slides/` resolved from Firebase Storage — and since that
 * path rejects names containing '/' (assertSafeFileName), the local-path
 * branch must come first.
 *
 * @param {string} fileName - A '/'-prefixed public path or a Storage filename
 * @returns {Promise<string>} - The resolved image URL
 */
export const resolveSlideUrl = async (fileName) => {
  if (typeof fileName === "string" && fileName.startsWith("/")) {
    return fileName;
  }
  return getWorkflowSlideUrl(fileName);
};

/**
 * Test-only: clear the URL cache between cases.
 * @internal
 */
export const clearVideoUrlCache = () => {
  urlCache.clear();
};
